import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  MuiThemeProvider,
  TextField,
  createMuiTheme,
  MenuItem,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  getNewsById,
  updateNews,
  postNews,
  deleteNews,
} from "../../../../api/news";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import Editor from "../../../components/editor/Editor";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import LogoSection from "../../../components/UploadLogo";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyNews() {
  return {
    title: {
      es: "",
      en: "",
      fr: "",
    },
    resume: {
      es: "",
      en: "",
      fr: "",
    },
    content: {
      es: "",
      en: "",
      fr: "",
    },
    logoURL: "",
    site: "",
  };
}

export default function EditNewsPage() {
  const history = useHistory();
  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  const newsId = useParams().id;
  const [news, setNews] = useState(getEmptyNews());

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  useEffect(() => {
    if (!newsId) {
      disableLoadingData();
      return;
    }

    getNewsById(newsId)
      .then((res) => {
        setNews(res.data);
        disableLoadingData();
      })
      .catch((error) => {
        alertError({ error: error, customMessage: "Could not get news." });
        history.push("/news");
      });
  }, [newsId, disableLoadingData, history]);

  function saveNews() {
    if (!newsId) {
      postNews(news, selectedLogo)
        .then((res) => {
          alertSuccess({
            title: "Saved!",
            customMessage: `News successfully created.`,
          });
          history.push(`/news`);
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: `Could not save the news`,
          });
        });
    } else {
      updateNews(newsId, news, selectedLogo)
        .then((res) => {
          alertSuccess({
            title: "Saved!",
            customMessage: "Changes successfully saved.",
          });
          history.push("/news");
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;
      if (!news[element]) news[element] = {};
      let newText = news[element];
      newText[lang] = event.target.value;
      setNews({ ...news, [element]: newText });
    } else setNews({ ...news, [element]: event.target.value });
  };

  const handleChangeEditor = (element, lang, value) => {
    if (lang) {
      if (value === " ") return;
      if (!news[element]) news[element] = {};
      let newText = news[element];
      newText[lang] = value;
      setNews({ ...news, [element]: newText });
    } else setNews({ ...news, [element]: value });
  };

  const renderMultilanguageTabContent = (lang) => {
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginTop: "1rem",
          }}
        >
          <TextField
            id="site"
            label="Site"
            value={news.site || ""}
            onChange={handleChange("site")}
            margin="normal"
            variant="outlined"
            required
            select
            className="col-3"
          >
            <MenuItem value="both">Blog & Market</MenuItem>
            <MenuItem value="blog">Only Blog</MenuItem>
            <MenuItem value="market">Only Market</MenuItem>
          </TextField>

          <TextField
            className="col-5"
            id="title"
            label="Title"
            value={(news.title && news.title[lang]) || ""}
            onChange={handleChange("title", lang)}
            margin="normal"
            variant="outlined"
            required
            style={{ flex: 1 }}
          />

          <div className="mt-2 col-4">
            <LogoSection
              labelTitle="Image"
              selectedLogo={selectedLogo}
              setSelectedLogo={setSelectedLogo}
              objectClass={news}
              setObjectClass={setNews}
              openPreviewDialog={openPreviewDialog}
              setOpenPreviewDialog={setOpenPreviewDialog}
            />
          </div>
        </div>
        <TextField
          id={`resume`}
          label="Resume"
          value={(news.resume && news.resume[lang]) || ""}
          onChange={handleChange("resume", lang)}
          margin="normal"
          variant="outlined"
          required
          multiline
          minRows={2}
          maxRows={4}
        />
        <Editor
          body={(news.content && news.content[lang]) || ""}
          setBody={(new_body) => handleChangeEditor("content", lang, new_body)}
          className="max-height mt-5"
          lang={lang}
          placeholder={"Enter news content here..."}
        />
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          <CardHeader title="Edit news"></CardHeader>
          <CardBody>
            <MultilanguageTabBlock
              multilanguageTabContent={renderMultilanguageTabContent}
            />
          </CardBody>
        </Card>
        <Button
          onClick={() => history.push("/news")}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Back
        </Button>
        <Button
          onClick={() => saveNews()}
          variant="outlined"
          color="primary"
          style={{ marginRight: "20px" }}
        >
          Save News
        </Button>
        {newsId && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Delete news
              </Button>
            </MuiThemeProvider>

            <ConfirmDialog
              title={`Are you sure you want to delete this news?`}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteNews(newsId)
                  .then((res) => {
                    alertSuccess({
                      title: "Deleted!",
                      customMessage: `News deleted successfully`,
                    });
                    history.push(`/news`);
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: `Could not delete news`,
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
